import { useState, useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { MousePointerClick, Loader2 } from 'lucide-react';
import axios from 'axios';
import { API_BASE_URL } from '../../utils/constants';

const publicApi = axios.create({ baseURL: API_BASE_URL });

async function getSharedEvents(token: string) {
  const res = await publicApi.get(`/share/${token}/events`);
  const raw = res.data as any;
  if (Array.isArray(raw)) return raw;
  if (raw?.events && Array.isArray(raw.events)) return raw.events;
  return [];
}

interface Props {
  token: string;
  autoPlay?: boolean;
}

export default function SessionReplayPublic({ token, autoPlay = false }: Props) {
  const playerContainerRef = useRef<HTMLDivElement>(null);
  const playerRef = useRef<any>(null);

  const { data: rawEvents, isLoading, error } = useQuery({
    queryKey: ['shared-events', token],
    queryFn: () => getSharedEvents(token),
  });

  const rrwebEvents = (rawEvents || [])
    .filter((e: any) => {
      if (e.data?.type !== undefined && e.data?.data !== undefined) return true;
      if (e.type !== undefined && e.timestamp !== undefined && e.data !== undefined) return true;
      return false;
    })
    .map((e: any) => {
      if (e.data?.data && e.data?.type !== undefined) return e.data;
      if (e.eventType !== undefined && e.data) return { type: e.eventType, timestamp: Number(e.timestamp), data: e.data };
      return e;
    })
    .sort((a: any, b: any) => (a.timestamp || 0) - (b.timestamp || 0));

  const sidebarEvents = (rawEvents || [])
    .filter((e: any) => {
      const t = e.type || e.eventType;
      return t !== 'rrweb' && t !== undefined;
    })
    .sort((a: any, b: any) => {
      const ta = a.timestamp || new Date(a.createdAt).getTime() || 0;
      const tb = b.timestamp || new Date(b.createdAt).getTime() || 0;
      return ta - tb;
    });

  useEffect(() => {
    if (!playerContainerRef.current || rrwebEvents.length === 0) return;

    if (playerRef.current) {
      playerContainerRef.current.innerHTML = '';
      playerRef.current = null;
    }

    import('rrweb-player').then((mod) => {
      const RRWebPlayer = mod.default || mod;
      // @ts-ignore
      import('rrweb-player/dist/style.css').catch(() => {});

      if (!playerContainerRef.current) return;

      try {
        const player = new RRWebPlayer({
          target: playerContainerRef.current,
          props: {
            events: rrwebEvents,
            width: playerContainerRef.current.clientWidth,
            height: playerContainerRef.current.clientHeight - 80,
            autoPlay,
            showController: true,
            speedOption: [1, 2, 4, 8],
            skipInactive: true,
            showWarning: false,
            showDebug: false,
          },
        });
        playerRef.current = player;
      } catch (err) {
        console.error('[Vantage] Failed to initialize rrweb-player:', err);
      }
    });

    return () => {
      if (playerRef.current) {
        try { playerRef.current.$destroy?.(); } catch {}
        playerRef.current = null;
      }
    };
  }, [rrwebEvents.length]);

  return (
    <div className="flex gap-4 h-[calc(100vh-14rem)]">
      {/* Player */}
      <div className="flex flex-1 flex-col">
        <div className="relative flex-1 overflow-hidden rounded-xl border border-gray-200 bg-gray-900">
          {isLoading && (
            <div className="flex h-full w-full items-center justify-center">
              <Loader2 className="h-8 w-8 animate-spin text-white" />
              <span className="ml-3 text-white">Loading recording...</span>
            </div>
          )}

          {error && (
            <div className="flex h-full w-full items-center justify-center">
              <p className="text-red-400">Failed to load session events</p>
            </div>
          )}

          {!isLoading && !error && rrwebEvents.length === 0 && (
            <div className="flex h-full w-full items-center justify-center text-center">
              <div>
                <p className="text-sm text-gray-400">No recording data available for this session</p>
              </div>
            </div>
          )}

          <div
            ref={playerContainerRef}
            className="h-full w-full"
            style={{ display: rrwebEvents.length > 0 ? 'block' : 'none' }}
          />
        </div>
      </div>

      {/* Events Sidebar */}
      <div className="w-64 flex-shrink-0 overflow-hidden rounded-xl border border-gray-200 bg-white">
        <div className="flex items-center gap-2 border-b border-gray-200 px-3 py-2.5">
          <MousePointerClick className="h-4 w-4 text-gray-400" />
          <span className="text-xs font-medium text-gray-600">Events</span>
        </div>
        <div className="overflow-y-auto h-[calc(100%-3rem)] p-2 space-y-1">
          {sidebarEvents.length === 0 && (
            <p className="p-4 text-center text-xs text-gray-400">No events</p>
          )}
          {sidebarEvents.map((event: any, i: number) => (
            <div key={event.id || i} className="flex items-start gap-2 rounded-lg p-2 text-xs hover:bg-gray-50">
              <span className="mt-0.5 shrink-0 text-gray-400">
                {event.timestamp
                  ? new Date(Number(event.timestamp)).toLocaleTimeString()
                  : event.createdAt
                    ? new Date(event.createdAt).toLocaleTimeString()
                    : '—'}
              </span>
              <div>
                <span className="font-medium text-gray-700">{event.type || event.eventType || 'event'}</span>
                {event.urlPath && <p className="mt-0.5 font-mono text-gray-400">{event.urlPath}</p>}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
